import React, { useEffect, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { toast, ToastContainer } from "react-toastify";
import { useTranslation } from "../i18n/I18nContext";
import { listUniforms, sellUniform, listSales } from "../services/uniform";
import WizardSteps from "./shared/WizardSteps";

const TOTAL_STEPS = 2;

const initialState = {
  uniformId: "",
  quantidade: "",
  estado: "Não Entregue",
};

const VendasUniformesLayer = () => {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(1);
  const [form, setForm] = useState(initialState);
  const [uniforms, setUniforms] = useState([]);
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const stepLabels = [t("venda.wizard.step.data"), t("venda.wizard.step.confirm")];

  const fetchSales = () => {
    setLoading(true);
    listSales()
      .then((resp) => setSales((resp.data || []).sort((a, b) => new Date(b.date || b.createdAt) - new Date(a.date || a.createdAt))))
      .catch(() => toast.error(t("venda.error.load")))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    listUniforms().then((resp) => setUniforms(resp.data || [])).catch(() => setUniforms([]));
    fetchSales();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedUniform = uniforms.find((u) => u.id === form.uniformId);

  const setField = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const nextStep = () => {
    if (!form.uniformId || !form.quantidade) {
      toast.error(t("venda.error.requiredFields"));
      return;
    }
    if (selectedUniform && Number(form.quantidade) > Number(selectedUniform.quantidade || 0)) {
      toast.error(t("venda.error.insufficientStock"));
      return;
    }
    if (currentStep < TOTAL_STEPS) setCurrentStep(currentStep + 1);
  };
  const prevStep = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };
  const restart = () => {
    setForm(initialState);
    setCurrentStep(1);
    setDone(false);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await sellUniform(form.uniformId, { quantidade: Number(form.quantidade), estado: form.estado });
      setDone(true);
      fetchSales();
      listUniforms().then((resp) => setUniforms(resp.data || [])).catch(() => {});
    } catch (err) {
      toast.error(err?.response?.data?.error || t("venda.error.create"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className='row gy-4'>
      <ToastContainer />

      <div className='col-12'>
        <div className='card'>
          <div className='card-body'>
            <h6 className='mb-4 text-xl'>{t("venda.wizard.title")}</h6>
            <p className='text-neutral-500'>{t("venda.wizard.subtitle")}</p>

            <div className='form-wizard'>
              <WizardSteps steps={stepLabels} currentStep={done ? TOTAL_STEPS + 1 : currentStep} />

              {!done && currentStep === 1 && (
                <fieldset className='wizard-fieldset show'>
                  <div className='row gy-3'>
                    <div className='col-sm-6'>
                      <label className='form-label'>{t("venda.field.article")}</label>
                      <select className='form-select' value={form.uniformId} onChange={setField("uniformId")} required>
                        <option value=''>{t("funcionario.select.placeholder")}</option>
                        {uniforms.map((u) => (
                          <option key={u.id} value={u.id}>
                            {u.artigo} — {u.genero}/{u.tamanho}{u.cor ? `/${u.cor}` : ""} ({t("artigo.col.available")}: {u.quantidade || 0})
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className='col-sm-3'>
                      <label className='form-label'>{t("venda.field.quantity")}</label>
                      <div className="icon-field">
                        <span className="icon">
                          <Icon icon="solar:box-minimalistic-outline" />
                        </span>
                        <input type='number' min='1' className='form-control' value={form.quantidade} onChange={setField("quantidade")} required />
                      </div>
                    </div>
                    <div className='col-sm-3'>
                      <label className='form-label'>{t("venda.field.status")}</label>
                      <select className='form-select' value={form.estado} onChange={setField("estado")}>
                        <option value='Não Entregue'>{t("venda.status.notDelivered")}</option>
                        <option value='Entregue'>{t("venda.status.delivered")}</option>
                      </select>
                    </div>
                  </div>
                  <div className='form-group d-flex align-items-center justify-content-end mt-24'>
                    <button type='button' className='form-wizard-next-btn btn btn-primary-600 px-32' onClick={nextStep}>
                      {t("global.next")} <i className='ri-arrow-right-line' />
                    </button>
                  </div>
                </fieldset>
              )}

              {!done && currentStep === 2 && (
                <fieldset className='wizard-fieldset show'>
                  <div className='alert alert-info'>{t("venda.confirm.description")}</div>
                  <ul className='list-unstyled d-flex flex-column gap-8'>
                    <li>
                      <strong>{t("venda.field.article")}:</strong> {selectedUniform?.artigo}
                    </li>
                    <li>
                      <strong>{t("venda.field.quantity")}:</strong> {form.quantidade}
                    </li>
                    <li>
                      <strong>{t("venda.field.total")}:</strong> {(Number(form.quantidade) * Number(selectedUniform?.preco || 0)).toFixed(2)}
                    </li>
                    <li>
                      <strong>{t("venda.field.status")}:</strong>{" "}
                      {form.estado === "Entregue" ? t("venda.status.delivered") : t("venda.status.notDelivered")}
                    </li>
                  </ul>
                  <div className='form-group d-flex align-items-center justify-content-end gap-8 mt-24'>
                    <button
                      type='button'
                      className='form-wizard-previous-btn btn btn-neutral-500 border-neutral-100 px-32'
                      onClick={prevStep}
                      disabled={submitting}
                    >
                      {t("global.previous")}
                    </button>
                    <button type='button' className='form-wizard-submit btn btn-primary-600 px-32' onClick={handleSubmit} disabled={submitting}>
                      {submitting ? t("global.loading") : (<><i className='ri-check-line' /> {t("global.confirm")}</>)}
                    </button>
                  </div>
                </fieldset>
              )}

              {done && (
                <fieldset className='wizard-fieldset show'>
                  <div className='text-center mb-40'>
                    <span className='w-64-px h-64-px bg-success-focus text-success-main rounded-circle d-inline-flex justify-content-center align-items-center mb-16 text-2xl'>
                      <i className='ri-check-line' />
                    </span>
                    <h6 className='text-md text-neutral-600'>{t("venda.success.created")}</h6>
                  </div>
                  <div className='form-group d-flex align-items-center justify-content-center gap-8'>
                    <button type='button' className='btn btn-neutral-500 border-neutral-100 px-32' onClick={restart}>
                      {t("venda.action.createAnother")}
                    </button>
                  </div>
                </fieldset>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className='col-12'>
        <div className='card radius-12'>
          <div className='card-body'>
            <h6 className='mb-16'>{t("venda.section.list")}</h6>
            {loading ? (
              <div className='text-center py-40'>{t("global.loading")}</div>
            ) : sales.length === 0 ? (
              <div className='alert alert-info mb-0'>{t("venda.hint.noSales")}</div>
            ) : (
              <div className='table-responsive'>
                <table className='table bordered-table mb-0'>
                  <thead>
                    <tr>
                      <th>{t("venda.field.article")}</th>
                      <th>{t("venda.field.quantity")}</th>
                      <th>{t("venda.field.total")}</th>
                      <th>{t("venda.field.status")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sales.slice(0, 20).map((s) => (
                      <tr key={s.id}>
                        <td>
                          {s.artigo} — {s.genero}/{s.tamanho}
                        </td>
                        <td>{s.quantidade}</td>
                        <td>{s.total}</td>
                        <td>
                          <span className={s.estado === "Entregue" ? "badge bg-success-focus text-success-main" : "badge bg-warning-focus text-warning-main"}>
                            {s.estado === "Entregue" ? t("venda.status.delivered") : t("venda.status.notDelivered")}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VendasUniformesLayer;
